/**
 * Главный файл для инициализации React компонентов
 * Импортирует и инициализирует все компоненты
 */

import { initImageSliders } from "./components/ImageSlider";
import { initPopupForms } from "./components/PopupForm";
import { initDynamicForms, initCostLeadForms, openDynamicForm } from "./components/DynamicForm";
import { initFeedbacks } from "./components/Feedback";
import IMask from "imask";

/**
 * Инициализация масок для полей телефона с использованием imask
 * Маска в стиле jQuery Inputmask: +7 (___) ___-__-__
 */
function initPhoneMasks(): void {
  const phoneInputs = document.querySelectorAll<HTMLInputElement>('[data-phone-input], input[type="tel"]');

  phoneInputs.forEach((input) => {
    // Создаем маску для российского формата телефона
    const mask = IMask(input, {
      mask: "+7 (000) 000-00-00",
      lazy: false, // Показываем маску сразу (не lazy)
      placeholderChar: "_", // Символ-плейсхолдер
    });

    // Обработка фокуса - если поле пустое, ставим курсор после +7
    input.addEventListener("focus", () => {
      if (!input.value || input.value.trim() === "" || input.value === "+7 (___) ___-__-__") {
        mask.value = "+7";
        input.setSelectionRange(2, 2);
      }
    });

    // Обработка blur - если только +7 или пусто, очищаем
    input.addEventListener("blur", () => {
      const cleanValue = input.value.replace(/\D/g, "");
      if (cleanValue === "7" || cleanValue === "" || input.value === "+7" || input.value === "+7 (___) ___-__-__") {
        mask.value = "";
      }
    });

    // Сохраняем ссылку на маску в элементе для возможного доступа позже
    (input as any).imask = mask;
  });
}

/**
 * Управление показом CAPTCHA - показывать только после заполнения обязательных полей
 */
function initCaptchaVisibility(): void {
  const forms = document.querySelectorAll<HTMLFormElement>('form[action="/form.php"]');

  forms.forEach((form) => {
    const formId = form.id;
    const captchaContainer = form.querySelector<HTMLElement>(`#${formId}-captcha-container`);
    if (!captchaContainer) return;

    // Находим обязательные поля (кроме CAPTCHA и consent)
    const requiredFields = form.querySelectorAll<HTMLInputElement>('input[required]:not([name="captcha"]):not([name="consent"])');

    const checkFields = (): void => {
      let allFilled = true;

      requiredFields.forEach((field) => {
        const value = field.value.trim();
        if (!value) {
          allFilled = false;
        } else if (field.type === "tel") {
          // Проверяем, что телефон заполнен полностью (минимум 10 цифр после +7)
          const digits = value.replace(/\D/g, "");
          if (digits.length < 11) {
            allFilled = false;
          }
        }
      });

      // Проверяем checkbox согласия
      const consentCheckbox = form.querySelector<HTMLInputElement>('input[name="consent"]');
      if (consentCheckbox && !consentCheckbox.checked) {
        allFilled = false;
      }

      // Показываем/скрываем CAPTCHA
      if (allFilled) {
        captchaContainer.classList.remove("hidden");
        const captchaInput = form.querySelector<HTMLInputElement>('input[name="captcha"]');
        if (captchaInput) {
          captchaInput.required = true;
        }
      } else {
        captchaContainer.classList.add("hidden");
        const captchaInput = form.querySelector<HTMLInputElement>('input[name="captcha"]');
        if (captchaInput) {
          captchaInput.required = false;
          captchaInput.value = "";
        }
      }
    };

    // Добавляем обработчики на все обязательные поля
    requiredFields.forEach((field) => {
      field.addEventListener("input", checkFields);
      field.addEventListener("change", checkFields);
    });

    // Обработчик для checkbox согласия
    const consentCheckbox = form.querySelector<HTMLInputElement>('input[name="consent"]');
    if (consentCheckbox) {
      consentCheckbox.addEventListener("change", checkFields);
    }

    // Первоначальная проверка
    checkFields();
  });
}

/**
 * Инициализация AJAX обработки для всех форм с action="/form.php"
 * Добавляет обработку отправки через AJAX с показом сообщений
 */
function initFormAjax(): void {
  const forms = document.querySelectorAll<HTMLFormElement>('form[action="/form.php"]');

  forms.forEach((form) => {
    // Пропускаем формы, которые уже обрабатываются React (popup формы)
    if (form.closest("[data-popup-form]") || form.dataset.ajaxHandled === "true") {
      return;
    }

    // Помечаем форму как обработанную
    form.dataset.ajaxHandled = "true";

    // Создаем контейнер для сообщений, если его нет
    let messageContainer = form.querySelector<HTMLElement>(".form-message-container");
    if (!messageContainer) {
      messageContainer = document.createElement("div");
      messageContainer.className = "form-message-container mt-4";
      form.appendChild(messageContainer);
    }

    // Находим кнопку отправки
    const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    const originalButtonText = submitButton?.textContent || "Отправить";

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      // Отключаем кнопку отправки
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = "Отправка...";
      }

      // Очищаем предыдущие сообщения
      messageContainer.innerHTML = "";
      messageContainer.className = "form-message-container mt-4";

      // Собираем данные формы
      const formData = new FormData(form);

      try {
        // Отправляем форму через AJAX
        const response = await fetch("/form.php", {
          method: "POST",
          body: formData,
        });

        const result = await response.json();

        if (result.success) {
          // Успешная отправка
          messageContainer.className = "form-message-container mt-4 p-4 bg-green-50 border border-green-200 rounded-lg";
          messageContainer.innerHTML = `
            <div class="flex items-center gap-2">
              <svg class="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <p class="text-green-800 text-sm font-medium">${result.message || "Спасибо! Ваша заявка принята. Мы свяжемся с вами в ближайшее время."}</p>
            </div>
          `;

          // Очищаем форму
          form.reset();

          // Обновляем CAPTCHA, если есть
          const captchaImage = form.querySelector<HTMLImageElement>('img[src*="captcha.php"]');
          if (captchaImage) {
            captchaImage.src = `/captcha.php?${Math.random()}`;
          }

          // Прокручиваем к сообщению
          messageContainer.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        } else {
          // Ошибка отправки
          messageContainer.className = "form-message-container mt-4 p-4 bg-red-50 border border-red-200 rounded-lg";
          messageContainer.innerHTML = `
            <div class="flex items-start gap-2">
              <svg class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div class="text-red-800 text-sm">
                <p class="font-medium mb-1">Ошибка отправки формы:</p>
                <p>${result.message || "Произошла ошибка. Пожалуйста, попробуйте позже или свяжитесь с нами по телефону."}</p>
              </div>
            </div>
          `;

          // Обновляем CAPTCHA при ошибке
          const captchaImage = form.querySelector<HTMLImageElement>('img[src*="captcha.php"]');
          if (captchaImage) {
            captchaImage.src = `/captcha.php?${Math.random()}`;
          }

          // Очищаем поле CAPTCHA
          const captchaInput = form.querySelector<HTMLInputElement>('input[name="captcha"]');
          if (captchaInput) {
            captchaInput.value = "";
          }

          // Прокручиваем к сообщению
          messageContainer.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      } catch (error) {
        console.error("Form submission error:", error);
        messageContainer.className = "form-message-container mt-4 p-4 bg-red-50 border border-red-200 rounded-lg";
        messageContainer.innerHTML = `
          <div class="flex items-start gap-2">
            <svg class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="text-red-800 text-sm">
              <p class="font-medium mb-1">Ошибка подключения:</p>
              <p>Произошла ошибка при отправке. Пожалуйста, проверьте подключение к интернету и попробуйте позже или позвоните нам напрямую.</p>
            </div>
          </div>
        `;
        messageContainer.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      } finally {
        // Восстанавливаем кнопку отправки
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalButtonText;
        }
      }
    });
  });
}

// Инициализация всех компонентов
function init(): void {
  initImageSliders();
  initPopupForms();
  initDynamicForms();
  initCostLeadForms();
  initFeedbacks();
  initPhoneMasks();
  initCaptchaVisibility();
  initFormAjax();
}

// Запуск при загрузке DOM (только для страниц Hugo, не для админки)
if (typeof window !== "undefined") {
  // Проверяем, не находимся ли мы на странице админки
  const isAdminPage = document.getElementById("admin-panel-container") !== null;

  if (!isAdminPage) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
    } else {
      init();
    }
  }
}

export { initImageSliders, initPopupForms, initDynamicForms, initCostLeadForms, openDynamicForm, initFeedbacks, initFormAjax };

// Делаем функции доступными глобально для совместимости
if (typeof window !== "undefined") {
  (window as any).initImageSliders = initImageSliders;
  (window as any).initPopupForms = initPopupForms;
  (window as any).initDynamicForms = initDynamicForms;
  (window as any).initCostLeadForms = initCostLeadForms;
  (window as any).openDynamicForm = openDynamicForm;
  (window as any).initFeedbacks = initFeedbacks;
  (window as any).initFormAjax = initFormAjax;
}
